import { AsyncPipe } from '@angular/common';
import { HttpEvent, HttpEventType, HttpResponse } from '@angular/common/http';
import { Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Breadcrumb } from '@app/api/core/decorator/breadcrumb.decorator';
import { GsmAbortDialogComponent } from '@app/shared/components/dialog/gsm-abort-dialog/gsm-abort-dialog.component';
import { MainContentComponent } from '@app/shared/components/main-content/main-content.component';
import { FormNachbringenComponent } from '@app/shared/gsm/gsm-ui-lib-elements/src/app/components/form-nachbringen/form-nachbringen.component';
import { FormChange, FormModelData } from '@app/shared/gsm/gsm-ui-lib-elements/src/app/components/form/form.model';
import { FileUploadResponse, FormDefinitionModel, FormUpdatedFileInstanceResponse } from '@app/shared/gsm/gsm-ui-lib-elements/src/app/dynamic-form-components/shared-models';
import { getAllFileInstances, removeFileInstances, updateFileInstances } from '@app/shared/gsm/gsm-ui-lib-elements/src/utils';
import { GsmWarnError } from '@app/shared/gsm/gsm-ui-lib-ng-shared/src/lib/gsm-error';
import { GsmErrorService } from '@app/shared/gsm/gsm-ui-lib-ng-shared/src/lib/gsm-error.service';
import { GsmService } from '@app/shared/gsm/gsm-ui-lib-ng-shared/src/lib/gsm.service';
import { RouterService } from '@app/shared/services/router.service';
import { ReactiveDialogService } from '@app/shared/services/ui/reactive-dialog.service';
import { SchadenUebersicht, UpdateForm } from '@gsm/entities';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { firstValueFrom, forkJoin, Observable, of, Subject } from 'rxjs';
import { filter, first, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { PageVertriebscockpitComponent } from '../../page/page.vertriebscockpit.component';
import { EventService } from '../event.service';

@Breadcrumb({
  name: 'Checkliste Nachbringen'
})

@UntilDestroy()
@Component({
  selector:    'app-checklist-item-nachbringen',
  templateUrl: './checklist-item-nachbringen.component.html',
  styleUrls:   ['./checklist-item-nachbringen.component.scss'],
  standalone:  true,
  imports:     [
    MainContentComponent,
    FormNachbringenComponent,
    AsyncPipe
  ]
})
export class ChecklistItemNachbringenComponent extends PageVertriebscockpitComponent implements OnInit {
  private routerService = inject(RouterService);
  private route = inject(ActivatedRoute);
  private gsmService = inject(GsmService);
  private gsmErrorService = inject(GsmErrorService);
  private eventService = inject(EventService);
  private dialogService = inject(ReactiveDialogService);

  schadenNummer: string;
  formDefinitionModels: FormDefinitionModel[];
  aktInfo: SchadenUebersicht;
  isLoading = false;
  isUploading = false;
  uploadBytesLoaded = 0;
  uploadBytesTotal = 0;
  backBtnClicked = false;

  public o30CockpitUrl$: Observable<string> = this.gsmService.getCockpitUrl();

  private _cancelUpload$ = new Subject();

  savedNachbringenDialogRefConfig = {
    maxWidth: '1000px',
    data:     {
      title:        'Checkliste',
      text_pre:     'Ihre Änderung wurde gespeichert.',
      buttonConfig: {
        visible:       true,
        actionButtons: [
          {
            name:  'Zurück zur Übersicht',
            color: 'accent'
          },
          {
            name:  'Weiteren Eintrag hinzufügen',
            color: 'accent'
          }
        ]
      }
    }
  };

  constructor() {
    super();

    this.llapi.patchValue('layout.pageLayout.routerOutletBg', false);
    this.llapi.patchValue('layout.pageLayout.breadcrumb', true);
    this.llapi.patchValue('layout.spinner', false);
  }

  ngOnInit() {
    const form$: Observable<UpdateForm> = this.route.paramMap.pipe(
      map(params => params.get('schadennummer')),
      tap(schadenNummer => {
        this.schadenNummer = schadenNummer;
      }),
      filter(schadenNummer => !!schadenNummer),
      switchMap(schadenNummer =>
        this.gsmService.getSchadenNachbringenForm(schadenNummer)
          .pipe(
            this.gsmErrorService.handleErrorAndReturnNull()
          )),
      first()
    );
    form$.subscribe(({
                       aktInfo,
                       taskList
                     }) => {
      forkJoin({
        aktInformation: of(aktInfo),
        userTasks:      of(taskList),
        gsmData:        this.llapi.getValue$('gsm')
                          .pipe(first())
      })
        .pipe(
          untilDestroyed(this)
        )
        .subscribe(async ({
                            aktInformation,
                            userTasks,
                            gsmData
                          }) => {
          if (!gsmData) {
            this.formDefinitionModels = [];
          }
          let storedInput;
          if (gsmData && gsmData.schadenNummer !== this.schadenNummer) {
            // ! do not use clearNachbringen here, since that would reset schadenNummer as well
            storedInput = [];
          } else {
            storedInput = gsmData
              && gsmData.currentNachbringenFormInput
              && gsmData.currentNachbringenFormInput.length
              && gsmData.currentNachbringenFormInput;
          }
          this.aktInfo = aktInformation;

          this.formDefinitionModels = await Promise.all((userTasks as FormDefinitionModel[]).map(async (userTask, index) =>
            await this.gsmService.mergeUserTaskAndInput(
              this.schadenNummer,
              userTask,
              storedInput[index]
            )
          ));
        });
    });
  }

  async onSubmit(formDefs: FormDefinitionModel<FormModelData>[]) {
    let hasMissingFiles = false;
    const models = await Promise.all(formDefs.map(async formDef => {
      const [clean, hasMissing] = await this.gsmService.checkUploadedFiles(this.schadenNummer, formDef);
      hasMissingFiles = hasMissingFiles || hasMissing;
      return clean;
    }));

    if (hasMissingFiles) {
      // * overwrite and "reload" via store
      this.formDefinitionModels = models.map(removeFileInstances);
      this.llapi.patchValue('gsm.currentNachbringenFormInput', this.formDefinitionModels);
      this.llapi.patchValue('gsm.schadenNummer', this.schadenNummer);
      throw new GsmWarnError('Eine oder mehrere Dateien konnten nicht mehr gefunden werden. Bitte laden Sie diese erneut hoch.');
    }

    const fileInstances: File[][] = await this._getAllFileInstances(formDefs);
    // has to be set after file instances to allow screenshot to be taken
    this.isLoading = true;

    this.isUploading = true;
    this.uploadBytesLoaded = 0;
    this.uploadBytesTotal = fileInstances
      .reduce((allFiles, files) => [
        ...allFiles,
        ...files
      ], [])
      .reduce((sum, file) => sum + file.size, 0);

    let index = 0;
    for (const formDef of formDefs) {
      let formData: FormModelData[];
      let uploadError: string;

      try {
        const uploadResult$ = this._uploadFiles(
          formDef.data,
          fileInstances[index]
        );
        const uploadResult = await firstValueFrom(uploadResult$);
        // If result is empty, upload was cancelled.
        if (!uploadResult) {
          this.isLoading = false;
          return;
        }
        formData = uploadResult.formData;
        uploadError = uploadResult.errorMessage;
      } catch (err) {
        this.gsmErrorService.catchError(err);
        this.isLoading = false;
        return;
      }

      formDef.data = formData;

      if (uploadError) {
        this.eventService.openVirusDialog(uploadError);
        this.isLoading = false;
        return;
      }

      index++;
    }

    this._resetUpload();

    try {
      const result$ = this.gsmService.postSchadenNachbringenForm(
        this.schadenNummer,
        formDefs
      );
      await firstValueFrom(result$);
      this.dialogService.open(GsmAbortDialogComponent, this.savedNachbringenDialogRefConfig)
        .afterClosed()
        .pipe(
          take(1)
        )
        .subscribe(result => {
          if (result && result.status === 'success') {
            this.routerService.routeGSMSchadenUebersicht(this.schadenNummer);
          }
        });
      const form$: Observable<UpdateForm> = this.gsmService.getSchadenNachbringenForm(this.schadenNummer)
        .pipe(this.gsmErrorService.handleErrorAndReturnNull());
      form$.subscribe(data => {
        this.formDefinitionModels = data?.taskList as FormDefinitionModel[];
        this.aktInfo = data?.aktInfo;
      });
    } catch (err) {
      this.gsmErrorService.catchError(err);
      this.isLoading = false;
      return;
    }

    this.isLoading = false;
  }

  onFormChange(event: FormChange): void {
    const {
      formModel,
      taskId
    } = event;
    const changed = this.formDefinitionModels.map(model => model.layoutId === taskId ? {
      ...model,
      data: formModel
    } : model);

    this.llapi.patchValue('gsm.currentNachbringenFormInput', changed.map(removeFileInstances));
    this.llapi.patchValue('gsm.schadenNummer', this.schadenNummer);
  }

  onBackToUebersicht() {
    this.backBtnClicked = true;
    this.clearNachbringen();
    this.routerService.routeGSMSchadenUebersicht(this.schadenNummer);
  }

  private _uploadFiles(
    formData: FormModelData[],
    fileInstances: File[]
  ): Observable<FormUpdatedFileInstanceResponse> {
    const previousBytesLoaded = this.uploadBytesLoaded;
    return this.gsmService.uploadFiles(this.schadenNummer, fileInstances)
      .pipe(
        takeUntil(this._cancelUpload$),
        tap((event: HttpEvent<FileUploadResponse>) => {
          if (event.type === HttpEventType.UploadProgress) {
            this.uploadBytesLoaded = previousBytesLoaded + event.loaded;
          }
        }),
        filter((event: HttpEvent<FileUploadResponse>) => event.type === HttpEventType.Response),
        map((event: HttpResponse<FileUploadResponse>) =>
          updateFileInstances(formData, event.body)
        )
      );
  }

  onCancelUpload() {
    this._cancelUpload$.next(null);
    this._resetUpload();
  }

  private _resetUpload() {
    this.isUploading = false;
    this.uploadBytesLoaded = 0;
    this.uploadBytesTotal = 0;
  }

  async onCustomEvent(event: CustomEvent) {
    this.eventService.onCustomEvent(event, this.schadenNummer);
  }

  private _getAllFileInstances(
    formDefs: FormDefinitionModel<FormModelData>[]
  ): Promise<File[][]> {
    return Promise.all(
      formDefs.map(formDef => getAllFileInstances(formDef.data))
    );
  }

  // TODO move nachbringen info to a separate object for more clarity
  private clearNachbringen() {
    this.llapi.resetValues([
      'gsm.schadenNummer',
      'gsm.currentNachbringenFormInput'
    ]);
  }
}
